import { forwardRef, useImperativeHandle, useRef } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { StyleSheet, View, ViewProps, ViewStyle } from 'react-native'
import { InsightWorkspace, useFloor, useWorkspaceTypes } from 'zens-store'
import {
  Button,
  Color,
  Font,
  Help,
  Icon,
  Radius,
  Spacing,
  Table,
  TableCol,
  TableRef,
  TableRowItem,
  TableSort,
  Text,
} from 'zens-storybook'

import { isFinite } from 'lodash'
import { heatmapColor } from '@dms/helper'
import { Link } from 'zens-router'

const styles = StyleSheet.create({
  container: {
    height: '100%',
  },
  title: {
    marginRight: Spacing.XS,
  },
  count: {
    fontFamily: Font.light,
    marginLeft: Spacing.XXS,
  },
  occupancy: {
    height: 64,
    flexDirection: 'row',
    alignItems: 'center',
  },
  bar: {
    height: 38,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: Radius.XS,
  },
  occupancyLarge: {
    color: Color.simplyWhite,
  },
  occupancySmall: {
    marginLeft: Spacing.XS,
  },
  table: {
    flex: 1,
  },
  head: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: Spacing.M,
  },
  workspaces: {
    marginLeft: Spacing.S,
    flexDirection: 'row',
    alignItems: 'center',
  },
  selected: {
    backgroundColor: Color.lightGreyBlue,
  },
  selectedText: {},
  selectedBar: {
    borderColor: Color.darkerBlue,
    borderWidth: 1,
  },
  help: {
    width: '100%',
  },
  helpText: {
    color: Color.simplyWhite,
  },
  helpTitle: {
    color: Color.simplyWhite,
    fontFamily: Font.medium,
  },
  row: {
    alignItems: 'flex-start',
  },
  rowContent: {
    height: 64,
    justifyContent: 'center',
  },
  details: {
    marginVertical: Spacing.XS,
    alignSelf: 'flex-end',
  },
})

export interface InsightsHeatmapTableRef {
  scrollToInsight: (params: {
    insight: InsightWorkspace
    animated?: boolean | null | undefined
    viewOffset?: number | undefined
    viewPosition?: number | undefined
  }) => void
}

interface Props extends ViewProps {
  companyId: number
  buildingId: number
  floorId: number
  insights?: InsightWorkspace[]
  selected?: string
  onSelect?: (id: number) => void
  onScrollToInsightFailed?: (info: {
    insight: InsightWorkspace
    highestMeasuredFrameIndex: number
    averageItemLength: number
  }) => void
  sort?: TableSort
  onSortChange?: (sort: TableSort) => void
}

const InsightsHeatmapTable = forwardRef<InsightsHeatmapTableRef, Props>((props, ref) => {
  const {
    companyId,
    buildingId,
    floorId,
    insights,
    selected,
    onSelect,
    onScrollToInsightFailed,
    sort,
    onSortChange,
    style,
    ...otherProps
  } = props
  const { t } = useTranslation()
  const tableRef = useRef<TableRef>(null)
  function isSelected(item: InsightWorkspace) {
    if (selected?.startsWith('#')) {
      const index = parseInt(selected.substring(1), 10)
      const value = item.value || 0
      return isFinite(item.value) && value >= index * 10 && value < (index + 1) * 10
    }
    return `${item.id}` === selected
  }

  useImperativeHandle(ref, () => ({
    scrollToInsight: (params) => {
      const { insight, ...otherParams } = params
      const row = rows.find((item) => item.data === insight)
      row && tableRef.current?.scrollToRow({ row, ...otherParams })
    },
  }))

  const floor = useFloor({ companyId, floorId })
  const types = useWorkspaceTypes()

  const renderName = (workspace: InsightWorkspace) => {
    const { name } = workspace
    return (
      <View style={styles.rowContent}>
        <Text level={5} style={isSelected(workspace) && styles.selectedText}>
          {name}
        </Text>
      </View>
    )
  }
  const renderType = (workspace: InsightWorkspace) => {
    const { workspaceTypeId } = workspace
    const type = types.data?.find((item) => item.id === workspaceTypeId)
    return (
      <View style={styles.rowContent}>
        <Text style={isSelected(workspace) && styles.selectedText}>{type?.name}</Text>
      </View>
    )
  }

  const renderOccupancy = (workspace: InsightWorkspace) => {
    const { value } = workspace
    const color = heatmapColor(value || 0)
    const barStyle: ViewStyle = {
      width: `${value || 0}%`,
      backgroundColor: color,
    }
    const rowSelected = isSelected(workspace)

    if (!isFinite(value)) return
    return (
      <View>
        <View style={styles.occupancy}>
          <View style={[styles.bar, barStyle, rowSelected && color === Color.lightGreyBlue && styles.selectedBar]}>
            {value != null && value >= 40 && (
              <Text level={5} style={styles.occupancyLarge}>
                {value} %
              </Text>
            )}
          </View>
          {value != null && value < 40 && (
            <Text level={5} style={[styles.occupancySmall, rowSelected && styles.selectedText]}>
              {value} %
            </Text>
          )}
        </View>
        {rowSelected && (
          <Link
            href={{
              pathname: `/companies/${companyId}/insights/${buildingId}/time`,
              params: {
                floorId,
                workspaceId: workspace.id,
              },
            }}
            asChild
          >
            <Button title={t('Show details')} light style={styles.details} testID="InsightsHeatmapTable.Details" />
          </Link>
        )}
      </View>
    )
  }

  const cols: TableCol<InsightWorkspace>[] = [
    {
      key: 'name',
      title: t('Name'),
      render: renderName,
    },
    {
      key: 'workspaceTypeId',
      title: t('Type'),
      render: renderType,
    },
    {
      key: 'value',
      title: t('Occupancy'),
      render: renderOccupancy,
    },
  ]

  const rows: TableRowItem<InsightWorkspace>[] = (
    insights?.map((workspace) => ({
      data: workspace,
      style: isSelected(workspace) && styles.selected,
      onPress: () => onSelect?.(workspace.data.id!),
      accessory: false as const,
    })) || []
  ).sort((a, b) => {
    if (sort?.field === 'value') {
      return ((a.data.value ?? -1) - (b.data.value ?? -1)) * (sort?.direction === 'desc' ? -1 : 1)
    } else if (sort?.field === 'workspaceTypeId')
      return (a.data.workspaceTypeId - b.data.workspaceTypeId) * (sort?.direction === 'desc' ? -1 : 1)
    else
      return (
        a.data.name?.localeCompare(b.data.name, undefined, {
          numeric: true,
          sensitivity: 'base',
        }) * (sort?.direction === 'desc' ? -1 : 1)
      )
  })

  const help = (
    <Text style={styles.helpText}>
      <Text style={styles.helpTitle}>
        <Trans>Name</Trans>:
      </Text>{' '}
      <Trans>The names of the workspaces that are located on this floor</Trans>
      {'\n'}
      <Text style={styles.helpTitle}>
        <Trans>Type</Trans>:
      </Text>{' '}
      <Trans>The type of the workspace</Trans>
      {'\n'}
      <Text style={styles.helpTitle}>
        <Trans>Occupancy</Trans>:
      </Text>{' '}
      <Trans>The percentage that the workspace has been used over the selected period of time</Trans>
    </Text>
  )

  return (
    <View style={[styles.container, style]} {...otherProps}>
      <View style={styles.head}>
        <Help
          style={styles.help}
          message={help}
          right={
            <View style={styles.workspaces}>
              <Icon name="desk" size="M" />
              <Text style={styles.count}>{insights?.length || 0}</Text>
            </View>
          }
        >
          <Text level={4} style={styles.title}>
            {floor.data?.displayName}
          </Text>
        </Help>
      </View>
      <Table
        ref={tableRef}
        rows={rows}
        cols={cols}
        condensed
        style={styles.table}
        rowStyle={styles.row}
        initialNumToRender={rows.length}
        onScrollToRowFailed={(info) => {
          const { row, ...otherInfo } = info
          const insight = row.data
          onScrollToInsightFailed?.({ insight, ...otherInfo })
        }}
        sort={sort}
        onSortChange={onSortChange}
        dataIsSorted
      />
    </View>
  )
})

export default InsightsHeatmapTable
